import { Column, StatusChip } from '@elementary/common-ui/components';
import { WAREHOUSE_DISPLAY_NAMES } from '@elementary/common-ui/constants/warehouses';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import { Tooltip, Typography } from '@mui/material';
import { formatRelativeTime } from '@elementary/common-ui/utils/datetime';
import { RUNTIME_CONNECTION_HEALTH_CHIP_STATUS } from '../constants';
import RemoveRowButton from '../RuntimeDetails/RemoveRowButton';
import { Runtime, RuntimeConnection } from '../types';
import { getConnectionErrorMessage, toUpdateRuntimeRequest } from '../utils';
import WarehouseLabel from '../WarehouseLabel';
import useRuntime from './useRuntime';

const useRuntimeConnectionsColumns = (runtime: Runtime) => {
  const hasAccountPermission = useAccountPermission();
  const { updateRuntime, isUpdating } = useRuntime(runtime.id);

  const removeConnection = (connectionId: string) => {
    const request = toUpdateRuntimeRequest(runtime);
    return updateRuntime({
      ...request,
      warehouseConnectionIds: request.warehouseConnectionIds.filter(
        (id) => id !== connectionId,
      ),
    });
  };

  const columns: Column<RuntimeConnection>[] = [
    {
      id: 'name',
      header: 'Name',
      filterable: false,
      renderer: (connection) => (
        <WarehouseLabel
          name={connection.name}
          warehouseType={connection.warehouseType}
        />
      ),
      compareFunc: (a, b) => a.name.localeCompare(b.name),
    },
    {
      id: 'warehouseType',
      width: 140,
      header: 'Type',
      filterable: false,
      renderer: (connection) => (
        <Typography>
          {WAREHOUSE_DISPLAY_NAMES[connection.warehouseType]}
        </Typography>
      ),
    },
    {
      id: 'lastVerifiedAt',
      width: 150,
      header: 'Last checked',
      filterable: false,
      showValueWhenEmpty: true,
      renderer: (connection) => (
        <Typography>
          {formatRelativeTime(connection.lastVerifiedAt, 'Not yet')}
        </Typography>
      ),
      compareFunc: (a, b) => dateComparer(a.lastVerifiedAt, b.lastVerifiedAt),
    },
    {
      id: 'health',
      width: 110,
      header: 'Status',
      sticky: 'right',
      filterable: false,
      renderer: (connection) => (
        <Tooltip
          title={getConnectionErrorMessage(connection) ?? ''}
          placement="top"
        >
          <span>
            <StatusChip
              status={RUNTIME_CONNECTION_HEALTH_CHIP_STATUS[connection.health]}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  const removeColumn: Column<RuntimeConnection> = {
    id: 'remove',
    sticky: 'right',
    width: 64,
    header: '',
    renderer: (connection) => (
      <RemoveRowButton
        tooltip="Remove from runtime"
        confirmTitle="Remove warehouse"
        confirmMessage={`The runtime will stop running queries for ${connection.name}. You can add it back at any time.`}
        confirmText="Remove"
        onConfirm={() => removeConnection(connection.id)}
        isLoading={isUpdating}
      />
    ),
    filterable: false,
    sortable: false,
    metaColumn: true,
  };

  return hasAccountPermission('EDIT_RUNTIMES')
    ? [...columns, removeColumn]
    : columns;
};

export default useRuntimeConnectionsColumns;
